import { z } from 'zod';

export function parseErrorMessage(error: unknown): string {
  const message =
    typeof error === 'string'
      ? error
      : error && typeof error === 'object' && 'message' in error
        ? error.message
        : undefined;
  return typeof message === 'string' && message.trim() ? message : 'An unknown error occurred';
}

export const validationIssueSchema = z.object({
  resource: z.string().startsWith('/api/'),
  field: z.string(),
  code: z.string(),
  label: z.string(),
  message: z.string(),
});
export type ValidationIssue = z.infer<typeof validationIssueSchema>;
export const fieldErrorSchema = validationIssueSchema.extend({
  resource: z.string().optional(),
  code: z.string().optional(),
  label: z.string().optional(),
});
export const problemSchema = z.object({
  type: z.literal('about:blank'),
  title: z.string(),
  status: z.number().int(),
  code: z.string(),
  detail: z.string(),
  resolution: z.string(),
  errors: z.array(fieldErrorSchema).optional(),
  retryAfter: z.number().int().positive().optional(),
});
export type Problem = z.infer<typeof problemSchema>;

export class ApplicationError extends Error {
  readonly problem: Problem;
  constructor(status: number, code: string, detail: string, resolution: string, errors?: Problem['errors']) {
    super(detail);
    this.name = 'ApplicationError';
    this.problem = {
      type: 'about:blank',
      title: code.replaceAll('_', ' '),
      status,
      code,
      detail,
      resolution,
      ...(errors ? { errors } : {}),
    };
  }
}

export function validationError(detail: string, errors?: Problem['errors']): ApplicationError {
  return new ApplicationError(
    422,
    'validation_failed',
    detail,
    'Correct the indicated fields and try again.',
    errors,
  );
}
export function notFoundError(): ApplicationError {
  return new ApplicationError(
    404,
    'not_found',
    'This object is unavailable.',
    'Check the object ID and your access.',
  );
}
